import React, { useEffect, useState } from "react";

import type { Props as LinearProgressProps } from "@busbud/design-system-components/dist/LinearProgress/LinearProgress";
import LinearProgress from "@busbud/design-system-components/dist/LinearProgress/LinearProgress";

import { useStyles } from "./linear-progress-bar.styles";

const MAX_PROGRESS = 95;

const getRndInteger = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min)) + min;

const useProgress = (
  initial_value: number,
  is_completed: boolean,
  quick_start: boolean
): number => {
  const [progress, setProgress] = useState(() => initial_value);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prevProgress => {
        if (is_completed) {
          clearInterval(timer);
          return 100;
        }

        if (quick_start) {
          if (prevProgress < 75) {
            return prevProgress + getRndInteger(12, 16);
          }
          if (prevProgress >= MAX_PROGRESS) {
            return prevProgress;
          }

          const additional_value = prevProgress + getRndInteger(0.2, 0.4);
          return additional_value > MAX_PROGRESS
            ? MAX_PROGRESS
            : additional_value;
        }

        // Keep at a max of 95% until complete
        if (prevProgress >= MAX_PROGRESS) {
          return prevProgress;
        }
        // Going faster on the first to second quarter
        if (prevProgress < 40) {
          return prevProgress + getRndInteger(1.2, 1.8);
        }

        // Going slow otherwise
        return prevProgress + getRndInteger(0.2, 0.4);
      });
    }, 200);

    return () => {
      clearInterval(timer);
    };
  }, [is_completed, quick_start]);

  return progress;
};

interface Props {
  className?: string;
  initial_value?: number;
  is_completed?: boolean;
  quick_start?: boolean;
  size?: LinearProgressProps["size"];
}

export const LinearProgressBar: React.FC<Props> = ({
  className,
  initial_value = 0,
  is_completed = false,
  quick_start = false,
  size = "md",
  ...restProps
}) => {
  const { cx, classes } = useStyles();
  const progress_value = useProgress(initial_value, is_completed, quick_start);

  return (
    <LinearProgress
      variant="determinate"
      className={cx(classes.root, className)}
      value={progress_value}
      size={size}
      {...restProps}
    />
  );
};
